import {
  Children,
  isValidElement,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { mergeRefs } from "react-merge-refs";
import {
  DndContext,
  DragEndEvent,
  DragMoveEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { useScrollLock } from "../../utils/hooks/useScrollLock";
import { Body } from "../body/Body";
import { H1 } from "../heading/Heading.Parts";
import {
  BottomSheetControl,
  BottomSheetToggleProvider,
  BottomSheetWrapper,
  Container,
  Content,
  MAX_RUBBER_BAND_DISTANCE,
  Overlay,
  Props,
  RUBBER_BAND_RESISTANCE,
  RUBBER_BAND_SENSITIVITY,
  getSnapGeometry,
  parseLengthToPixels,
  percentToPixels,
  useEscapeKey,
} from "./BottomSheet.Parts";
import { Handle } from "./handle/Handle";

export const BottomSheet = ({
  defaultOpen,
  open,
  afterClose,
  onOpenChange,
  height,
  snapPoints,
  draggable = true,
  dragThreshold = 15, // 15% of screen height
  hiddenTitle,
  titleId,
  hiddenDescription,
  descriptionId,
  controlRef,
  zIndex,
  children,
  ref,
  ...props
}: Props) => {
  // #region State Variables, Constants & Defaults
  const [yOffset, setYOffset] = useState(0);
  const [dragProgress, setDragProgress] = useState(0); // -1 (prev snap) to 1 (next snap)
  const [isDragging, setIsDragging] = useState(false);
  const [isOpening, setIsOpening] = useState(false);
  const [overlayVisible, setOverlayVisible] = useState(false);

  const defaultSnapPoint = snapPoints?.findIndex((p) => p.isDefault) ?? 0;
  const validDefaultSnapPoint = defaultSnapPoint === -1 ? 0 : defaultSnapPoint;
  const [activeSnapPoint, setActiveSnapPoint] = useState<number>(
    validDefaultSnapPoint,
  );
  const activeSnapHeight = snapPoints?.[activeSnapPoint]?.height;
  const activeSnapDragHandleBackgroundColor =
    snapPoints?.[activeSnapPoint]?.dragHandleBackgroundColor;
  const activeSnapShowOverlay =
    snapPoints?.[activeSnapPoint]?.showOverlay ?? true; // When snap points aren't defined, we default to showing the overlay for single snap behavior

  const {
    floorResistancePx,
    nextSnapHeight,
    prevSnapHeight,
    dragTargetHeight,
  } = getSnapGeometry(
    snapPoints,
    activeSnapPoint,
    activeSnapHeight,
    dragProgress,
  );

  const activeChild = snapPoints
    ? Children.toArray(children).find((c) => {
        if (!isValidElement(c) || !c.key) return false;
        const cleanKey = c.key.toString().replace(/^\.\$/, ""); // Remove .$ prefix that React adds to keys in arrays
        return cleanKey === snapPoints[activeSnapPoint].key;
      })
    : children;

  const [shouldRender, setShouldRender] = useState(open ?? false); // We track local visibility to allow the exit transition to finish
  const contentRef = useRef<HTMLDivElement>(null);
  // Latest afterClose, read when the close finishes - callers can pass a new
  // function every render without restarting the close timer.
  const afterCloseRef = useRef(afterClose);
  useEffect(() => {
    afterCloseRef.current = afterClose;
  });
  const isDraggingRef = useRef(false); // synchronous mirror of isDragging
  const isClosing = !open && shouldRender;

  const homeSnapPoint = snapPoints?.findIndex((p) => p.home) ?? -1;
  const hasHomeSnapPoint = homeSnapPoint !== -1;
  const isAtHomeSnapPoint = activeSnapPoint === homeSnapPoint;

  const id = useId();

  const labelledBy = activeSnapShowOverlay
    ? titleId || (hiddenTitle ? `${id}-title` : undefined)
    : undefined;

  const describedBy =
    activeSnapShowOverlay && hiddenDescription
      ? `${id}-description`
      : undefined;
  // #endregion

  useScrollLock(shouldRender && activeSnapShowOverlay);

  useEscapeKey(() => {
    if (hasHomeSnapPoint && !isAtHomeSnapPoint)
      setActiveSnapPoint(homeSnapPoint);
    else onOpenChange?.(false);
  }, shouldRender && activeSnapShowOverlay);

  useImperativeHandle(
    controlRef,
    (): BottomSheetControl => ({
      snapToFirst: () => setActiveSnapPoint(0),
    }),
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 10 }, // Prevents accidental drags on taps
    }),
  );

  // #region Effects
  useEffect(() => {
    if (activeSnapShowOverlay)
      // Show overlay immediately
      setOverlayVisible(true);
    else {
      // Hide overlay with a delay to allow transition
      const timer = setTimeout(() => {
        setOverlayVisible(false);
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [activeSnapShowOverlay]);

  useEffect(() => {
    // Focus the content so screen readers and keyboard users know it opened
    if (open && shouldRender && contentRef.current && activeSnapShowOverlay) {
      contentRef.current.focus();
    }
  }, [open, shouldRender, activeSnapShowOverlay]);

  useEffect(() => {
    if (open) {
      // Opening Sequence (Triggered by prop change)
      setYOffset(window.innerHeight);
      setShouldRender(true);
      setIsOpening(true);
      setActiveSnapPoint(validDefaultSnapPoint); // Reset to default on fresh open

      const timer = setTimeout(() => {
        setYOffset(0);
      }, 50); // Small delay to ensure the initial position is rendered

      const endTimer = setTimeout(() => setIsOpening(false), 700);

      return () => {
        clearTimeout(timer);
        clearTimeout(endTimer);
      };
    } else if (shouldRender) {
      // Closing Sequence (Triggered by swipe OR overlay click)
      setIsDragging(false);
      setYOffset(window.innerHeight); // This triggers the ease-out transition

      const timer = setTimeout(() => {
        setShouldRender(false);
        setYOffset(0);
        afterCloseRef.current?.();
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [open, shouldRender, validDefaultSnapPoint]);
  // #endregion

  // #region Handlers
  const handleDragMove = (event: DragMoveEvent) => {
    if (!draggable || !isDraggingRef.current) return;

    const { y } = event.delta;
    const isAtMaxSnap = snapPoints && activeSnapPoint === snapPoints.length - 1;

    if (y < 0 && (!snapPoints || isAtMaxSnap)) {
      // Already at the tallest height — nudge it up slightly instead of growing
      const rubberBandValue =
        Math.atan(-y / RUBBER_BAND_SENSITIVITY) *
        MAX_RUBBER_BAND_DISTANCE *
        RUBBER_BAND_RESISTANCE;
      setYOffset(-rubberBandValue);
      setDragProgress(0);
    } else if (snapPoints) {
      // Scale by the real pixel gap between snap heights, not dragThreshold
      const isAtMinSnap = activeSnapPoint === 0;
      const realTargetHeight =
        y < 0 ? nextSnapHeight : !isAtMinSnap ? prevSnapHeight : undefined;
      const realDistancePx = realTargetHeight
        ? Math.abs(
            (parseLengthToPixels(realTargetHeight) ?? 0) -
              (parseLengthToPixels(activeSnapHeight) ?? 0),
          )
        : 0;
      const distancePx = realDistancePx || floorResistancePx;

      setYOffset(0);
      setDragProgress(Math.max(-1, Math.min(1, -y / distancePx)));
    } else {
      setYOffset(y);
      setDragProgress(0);
    }

    setIsDragging(true);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    if (!draggable) return;
    isDraggingRef.current = false;

    const { y } = event.delta;
    const thresholdPx = percentToPixels(dragThreshold); // Convert percentage to pixels

    let nextIdx = activeSnapPoint;
    let shouldClose = false;

    if (snapPoints) {
      if (y > thresholdPx) {
        // Dragging DOWN - go to previous (smaller) snap point
        if (activeSnapPoint > 0) nextIdx = activeSnapPoint - 1;
        else shouldClose = true; // Only close if we're already at the first (smallest) snap point
      } else if (y < -thresholdPx) {
        // Dragging UP - go to next (larger) snap point
        if (activeSnapPoint < snapPoints.length - 1)
          nextIdx = activeSnapPoint + 1;
        // Don't close when dragging up, just stay at max snap point
      }
    } else if (y > thresholdPx) shouldClose = true; // No snap points - only close when dragging down past threshold

    setIsDragging(false);
    setYOffset(0);
    setDragProgress(0);

    if (shouldClose) {
      onOpenChange?.(false);
    } else if (nextIdx !== activeSnapPoint) {
      // Only change snap point if we're actually moving to a different one
      setActiveSnapPoint(nextIdx);
    }
  };

  const handleDragStart = () => {
    if (!draggable) return;
    isDraggingRef.current = true;
    setYOffset(0);
    setDragProgress(0);
    setIsDragging(true);
  };

  const handleDragCancel = () => {
    if (!draggable) return;
    isDraggingRef.current = false;
    setIsDragging(false);
    setYOffset(0);
    setDragProgress(0);
  };

  const handleOverlayClick = (event: React.MouseEvent) => {
    if (event.target !== event.currentTarget) return;

    // Only handle overlay clicks when overlay is shown (modal behavior)
    if (!activeSnapShowOverlay) return;

    if (hasHomeSnapPoint && !isAtHomeSnapPoint)
      setActiveSnapPoint(homeSnapPoint);
    else onOpenChange?.(false);
  };

  // Tapping the handle: climb one snap point at a time; once at the largest,
  // jump to home; with no home (or no snap points at all), close instead.
  const toggleSnapPoint = () => {
    if (!snapPoints) onOpenChange?.(false);
    else if (activeSnapPoint < snapPoints.length - 1)
      setActiveSnapPoint(activeSnapPoint + 1);
    else if (hasHomeSnapPoint) setActiveSnapPoint(homeSnapPoint);
    else onOpenChange?.(false);
  };
  // #endregion

  // If the component shouldn't be rendered at all, return null
  if (!shouldRender) return null;

  const content = (
    <Content
      $yOffset={yOffset}
      $dragProgress={dragProgress}
      $dragTargetHeight={dragTargetHeight}
      $isDragging={isDragging}
      $isOpening={isOpening}
      $open={open}
      $height={activeSnapHeight || height}
      $showOverlay={activeSnapShowOverlay}
      $zIndex={zIndex}
      role={activeSnapShowOverlay ? "dialog" : undefined}
      aria-modal={activeSnapShowOverlay ? "true" : undefined}
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
      tabIndex={activeSnapShowOverlay ? -1 : undefined}
      ref={mergeRefs([contentRef, ref])}
      {...props}
    >
      <BottomSheetToggleProvider value={{ toggleSnapPoint }}>
        {draggable && (
          <Handle backgroundColor={activeSnapDragHandleBackgroundColor} />
        )}
        {hiddenTitle && activeSnapShowOverlay && !titleId && (
          <VisuallyHidden>
            <H1 id={`${id}-title`}>{hiddenTitle}</H1>
          </VisuallyHidden>
        )}
        {hiddenDescription && activeSnapShowOverlay && !descriptionId && (
          <VisuallyHidden>
            <Body id={`${id}-description`}>{hiddenDescription}</Body>
          </VisuallyHidden>
        )}
        <Container>{activeChild}</Container>
      </BottomSheetToggleProvider>
    </Content>
  );

  return createPortal(
    <BottomSheetWrapper $isOpen={shouldRender} $zIndex={zIndex}>
      {overlayVisible && (
        <Overlay
          $isClosing={isClosing}
          $isOpen={!!open}
          $showOverlay={activeSnapShowOverlay}
          onClick={handleOverlayClick}
        />
      )}
      {draggable ? (
        <DndContext
          sensors={sensors}
          modifiers={[restrictToVerticalAxis]}
          onDragStart={handleDragStart}
          onDragMove={handleDragMove}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          {content}
        </DndContext>
      ) : (
        content
      )}
    </BottomSheetWrapper>,
    document.body,
  );
};
